// src/modules/app-views/invoices/pages/Document/actions/dropdownMenu.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { MoreOutlined, ImportOutlined, DeleteOutlined, InboxOutlined } from '@ant-design/icons';
import {
  dropdownNew,
  dropdownDuplicate,
  dropdownDivider,
  dropdownRestore,
  dropdownRemovePermanently,
} from 'layouts/Pages/PageHeader/dropdownMenus';
import { isLockedStatus } from 'services/fiscalService';
import appService from 'services/appService';
import axiosService from 'services/axiosService';
import translateX from 'utils/translateX';
import { API } from 'constants/Api';

// *******************************************************************************
// CUSTOM DROPDOWN ITEMS FOR INVOICES
// *******************************************************************************

// Divider that hides when no migration tokens configured
const migrationDivider = {
  ...dropdownDivider,
  key: 'migration-divider',
  hidden: ({ storedSubscription }) => {
    const tokens = storedSubscription?.migrationTokens || [];
    return tokens.length === 0;
  },
};

// Reimport Document - reimport from legacy system
const invoiceDropdownReimport = {
  key: 'reimport',
  label: 'reimport_document',
  icon: <ImportOutlined />,
  // Hide if no migration tokens configured in subscription
  hidden: ({ storedSubscription }) => {
    const tokens = storedSubscription?.migrationTokens || [];
    return tokens.length === 0;
  },
  disabled: ({ currentPage }) => {
    const data = currentPage?.data;
    // Disable if no legacyCode or if status is locked (non-editable)
    if (!data?.legacyCode) return true;
    return isLockedStatus(data?.status);
  },
  onClick: async ({ page, refreshDocument }) => {
    const data = page?.data;
    const legacyCode = data?.legacyCode;

    if (!legacyCode) {
      appService.message({ flag: 'w', text: translateX('reimport_no_legacy_code') });
      return;
    }

    // Show loading message
    appService.message({ flag: 'loading', text: translateX('reimporting_document') });

    try {
      // Call the reimport API with forceOverwrite (always overwrite from document menu)
      const response = await axiosService.post({
        url: API.MIGRATION.REIMPORT,
        data: {
          legacyCode: String(legacyCode),
          collection: 'nfe',
          forceOverwrite: true, // Reimport from document always overwrites
        },
      });

      // Response is an array: [responseData, metadata]
      const [result] = response || [];

      appService.messageDestroy();

      // Handle success scenarios
      if (result?.success) {
        appService.notification({
          flag: 'success',
          title: translateX('reimport_success_title'),
          description: translateX('reimport_success_description', {
            number: legacyCode,
          }),
          duration: 6,
        });

        // Refresh document after successful reimport
        if (typeof refreshDocument === 'function') {
          refreshDocument();
        }
      } else {
        // Handle error codes from response (HTTP 200 but logical error)
        const errorCode = result?.errorCode;

        if (errorCode === 'UPDATE_NOT_ALLOWED') {
          appService.notification({
            flag: 'warning',
            title: translateX('document_imported_failure'),
            description: translateX('document_imported_failure_description', {
              documentNumber: <strong>{legacyCode}</strong>,
            }),
            duration: 10,
          });
          // } else if (errorCode === 'DOCUMENT_NOT_FOUND') {
          //   appService.notification({
          //     flag: 'warning',
          //     title: translateX('import_order_not_found_title'),
          //     description: translateX('import_order_not_found_description', {
          //       number: legacyCode,
          //     }),
          //     duration: 8,
          //   });
        } else {
          appService.notification({
            flag: 'warning',
            title: translateX('reimport_error_title'),
            description: translateX('document_not_found_description', {
              documentNumber: <strong>{legacyCode}</strong>,
            }),
            duration: 8,
          });

          // appService.notification({
          //   flag: 'warning',
          //   title: translateX('reimport_error_title'),
          //   description: result?.message || translateX('unknown_error'),
          //   duration: 8,
          // });
        }
      }
    } catch (error) {
      appService.messageDestroy();

      const errorData = error?.response?.data?.data || error?.response?.data || {};
      const errorCode = errorData?.errorCode;

      if (errorCode === 'UPDATE_NOT_ALLOWED') {
        appService.notification({
          flag: 'warning',
          title: translateX('document_imported_failure'),
          description: translateX('document_imported_failure_description', {
            documentNumber: <strong>{legacyCode}</strong>,
          }),
          duration: 10,
        });

        //   appService.notification({
        //     flag: 'warning',
        //     title: translateX('import_update_not_allowed_title'),
        //     description: translateX('import_update_not_allowed_description', {
        //       documentNumber: errorData.documentNumber || '-',
        //       serie: errorData.documentSerie || '-',
        //       status: errorData.existingStatusLabel || errorData.existingStatus || '-',
        //       recipient: errorData.recipientName || '-',
        //       issuer: errorData.issuerName || '-',
        //     }),
        //     duration: 10,
        //   });
        // } else if (errorCode === 'DOCUMENT_NOT_FOUND') {
        //   appService.notification({
        //     flag: 'warning',
        //     title: translateX('import_order_not_found_title'),
        //     description: translateX('import_order_not_found_description', {
        //       number: legacyCode,
        //     }),
        //     duration: 8,
        //   });
      } else {
        const errorMessage = errorData?.message || error?.message;
        appService.notification({
          flag: 'error',
          title: translateX('reimport_error_title'),
          description: errorMessage || translateX('unknown_error'),
          duration: 8,
        });
      }
    }
  },
};

// Archive - disabled for locked statuses (SEFAZ processed documents)
const invoiceDropdownArchive = {
  key: 'archive',
  label: 'archive_document',
  icon: <InboxOutlined />,
  hidden: ({ currentPage }) => !!currentPage?.data?._metadata?.audit?.removedAt,
  disabled: ({ currentPage }) => {
    const data = currentPage?.data;
    if (!data?._id) return true;
    // Disable for locked statuses (authorized, canceled, voided, denied, corrected)
    return isLockedStatus(data?.status);
  },
  onClick: ({ handleArchiveDocuments, page, handleListRefresh }) => {
    const data = page?.data;
    if (!data?._id) return;
    handleArchiveDocuments?.([data], { onSuccess: handleListRefresh });
  },
};

// Remove - disabled for locked statuses (cannot delete SEFAZ processed documents)
const invoiceDropdownRemove = {
  key: 'remove',
  label: 'remove',
  icon: <DeleteOutlined />,
  hidden: ({ currentPage }) => !!currentPage?.data?._metadata?.audit?.removedAt,
  disabled: ({ currentPage }) => {
    const data = currentPage?.data;
    if (!data?._id) return true;
    // Disable for locked statuses (authorized, canceled, voided, denied, corrected)
    return isLockedStatus(data?.status);
  },
  danger: true,
  onClick: ({ handleRemoveDocuments, page, handleListRefresh }) => {
    const data = page?.data;
    if (!data?._id) return;
    handleRemoveDocuments?.([data], { onSuccess: handleListRefresh });
  },
};

// *******************************************************************************
// DROPDOWN MENU
// *******************************************************************************
const invoicesDocumentDropdownMenu = () => {
  return {
    action: 'dropdownMenu',
    icon: <MoreOutlined />,
    tooltip: 'more_options',

    dropdownItems: [
      // Standard actions
      dropdownNew,
      dropdownDuplicate,

      // Migration section (hidden if no tokens)
      migrationDivider,
      invoiceDropdownReimport,

      /*
      dropdownDivider,

      // Download PDF
      {
        key: 'download-pdf',
        labelKey: 'download_pdf',
        icon: null,
        show: ({ data }) => {
          const status = data?.status;
          return status === 'authorized' && data?.urls?.pdf;
        },
        onClick: ({ data }) => {
          if (data?.urls?.pdf) {
            window.open(data.urls.pdf, '_blank');
          }
        },
      },

      // Download
      {
        key: 'download-xml',
        labelKey: 'download_xml',
        icon: null,
        show: ({ data }) => {
          const status = data?.status;
          return status === 'authorized' && data?.urls?.xml;
        },
        onClick: ({ data }) => {
          if (data?.urls?.xml) {
            window.open(data.urls.xml, '_blank');
          }
        },
      },

      // Print DANFE
      {
        key: 'print-danfe',
        labelKey: 'print_danfe',
        icon: null,
        show: ({ data }) => {
          const status = data?.status;
          return status === 'authorized' && data?.urls?.danfe;
        },
        onClick: ({ data }) => {
          if (data?.urls?.danfe) {
            window.open(data.urls.danfe, '_blank');
          }
        },
      },

      // Cancel invoice item (custom) - only for authorized invoices
      {
        key: 'cancel-invoice',
        labelKey: 'cancel_invoice',
        icon: null,
        danger: true,
        show: ({ data }) => {
          const status = data?.status;
          return status === 'authorized';
        },
        onClick: async ({ data, refreshData, showConfirm }) => {
          const confirmed = await showConfirm({
            title: 'cancel_invoice_confirm_title',
            description: 'cancel_invoice_confirm_description',
            danger: true,
          });

          if (confirmed) {
            // TODO: Implement cancel invoice API call
            console.log('Cancel invoice:', data?._id);
          }
        },
      },

      */

      dropdownDivider,
      invoiceDropdownArchive,
      invoiceDropdownRemove,
      dropdownRestore,
      dropdownRemovePermanently,
    ],
  };
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default invoicesDocumentDropdownMenu;
